import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  Video,
  Sparkles,
  Play,
  User,
  Film,
  RefreshCw,
  Edit3,
  Check,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Wand2,
  Download,
  Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface VideoConcept {
  title: string;
  platform: string;
  script: string;
  scenes: Array<{ description: string; duration: number; textOverlay?: string }>;
  targetAudience?: string;
  tone?: string;
  withPresenter?: boolean;
  estimatedDuration?: number;
  avatarId?: string;
  voiceId?: string;
  aspectRatio?: string;
}

interface VideoJob {
  id: number;
  workspaceId: number;
  status: string;
  mode: string;
  engine: string;
  concept: VideoConcept | null;
  videoUrl: string | null;
  thumbnailUrl: string | null;
  duration: number | null;
  engineJobId: string | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

interface HeyGenAvatar {
  avatar_id: string;
  avatar_name: string;
  gender: string;
  preview_image_url: string;
}

interface HeyGenStatus {
  videoId: string;
  status: "pending" | "processing" | "rendering" | "completed" | "failed";
  mp4Url?: string;
  thumbnailUrl?: string;
  error?: string;
}

const VOICE_OPTIONS = [
  { id: "en-US-JennyNeural", label: "Jenny (Female, US)" },
  { id: "en-US-GuyNeural", label: "Guy (Male, US)" },
  { id: "en-US-AriaNeural", label: "Aria (Female, US)" },
  { id: "en-US-DavisNeural", label: "Davis (Male, US)" },
  { id: "en-GB-SoniaNeural", label: "Sonia (Female, UK)" },
  { id: "en-GB-RyanNeural", label: "Ryan (Male, UK)" },
  { id: "en-AU-NatashaNeural", label: "Natasha (Female, AU)" },
];

const PLATFORMS = ["Instagram", "Meta", "LinkedIn", "X", "Google"];
const DURATIONS = [15, 30, 60, 90];

function HeyGenStudio() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const urlParams = new URLSearchParams(window.location.search);
  const prefillScript = urlParams.get("script") || "";
  const prefillBrief = urlParams.get("brief") || "";
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(null);
  const [script, setScript] = useState(prefillScript);
  const [brief, setBrief] = useState(prefillBrief);
  const [bgColor, setBgColor] = useState("#0A0A0F");
  const [logoUrl, setLogoUrl] = useState("");
  const [duration, setDuration] = useState(30);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [videoStatus, setVideoStatus] = useState<HeyGenStatus | null>(null);
  const [mp4Url, setMp4Url] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: avatarData, isLoading: loadingAvatars } = useQuery<{ configured: boolean; avatars: HeyGenAvatar[] }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "heygen", "avatars"],
    queryFn: async () => {
      const r = await fetch(`/api/workspaces/${activeWorkspaceId}/heygen/avatars`, { credentials: "include" });
      if (r.status === 422) {
        const body = await r.json();
        return { configured: false, avatars: [], message: body.message };
      }
      const avatars = await r.json();
      return { configured: true, avatars: Array.isArray(avatars) ? avatars : [] };
    },
    enabled: !!activeWorkspaceId,
    staleTime: 60 * 60 * 1000,
  });

  const generateScriptMutation = useMutation({
    mutationFn: async () => {
      if (!brief.trim() || brief.length < 10) throw new Error("Enter a brief of at least 10 characters");
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/heygen/generate-script`, { brief, duration });
      return res.json();
    },
    onSuccess: (data) => {
      setScript(data.script || "");
      toast({ title: "Script generated", description: "Review and edit before generating the video." });
    },
    onError: (err: any) => toast({ title: "Script generation failed", description: err.message, variant: "destructive" }),
  });

  const generateVideoMutation = useMutation({
    mutationFn: async () => {
      if (!selectedAvatarId) throw new Error("Select an avatar first");
      if (!script.trim()) throw new Error("Script is required");
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/heygen/generate`, {
        avatarId: selectedAvatarId,
        script: script.trim(),
        backgroundHex: bgColor,
        logoUrl: logoUrl || undefined,
      });
      return res.json();
    },
    onSuccess: (data) => {
      setVideoId(data.videoId);
      setVideoStatus({ videoId: data.videoId, status: "rendering" as HeyGenStatus["status"] });
      setMp4Url(null);
      toast({ title: "Video generation started", description: "This takes 30–120 seconds. Polling for status…" });
    },
    onError: (err: any) => toast({ title: "Video generation failed", description: err.message, variant: "destructive" }),
  });

  useEffect(() => {
    if (!videoId || !activeWorkspaceId) return;
    const doneRef = { current: false };
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/workspaces/${activeWorkspaceId}/heygen/status/${videoId}`, { credentials: "include" });
        const status: HeyGenStatus = await res.json();
        setVideoStatus(status);
        if (status.status === "completed") {
          doneRef.current = true;
          setMp4Url(status.mp4Url || null);
          clearInterval(pollRef.current!);
          toast({ title: "Video ready!", description: "Your HeyGen video is ready to preview and download." });
        } else if (status.status === "failed") {
          doneRef.current = true;
          clearInterval(pollRef.current!);
          toast({ title: "Video generation failed", description: status.error || "Unknown error", variant: "destructive" });
        }
      } catch {}
    }, 10_000);
    const timeout = setTimeout(() => {
      clearInterval(pollRef.current!);
      if (!doneRef.current) {
        toast({ title: "Timeout", description: "Video generation is taking longer than expected. Check back later.", variant: "destructive" });
      }
    }, 5 * 60 * 1000);
    return () => { clearInterval(pollRef.current!); clearTimeout(timeout); };
  }, [videoId, activeWorkspaceId]);

  const saveToLibrary = useMutation({
    mutationFn: async () => {
      if (!mp4Url) throw new Error("No video URL");
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content`, {
        platform: "Instagram",
        contentType: "video",
        caption: script.slice(0, 200),
        mediaUrl: mp4Url,
        status: "draft",
        scheduledAt: null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
      toast({ title: "Saved to Library", description: "Video saved as a draft in Content Studio." });
    },
    onError: (err: any) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const avatars = avatarData?.avatars || [];
  const configured = avatarData?.configured !== false;

  if (!configured && !loadingAvatars) {
    return (
      <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-6 text-center" data-testid="heygen-not-configured">
        <Video size={36} className="mx-auto mb-3 text-amber-400 opacity-60" />
        <p className="text-sm font-semibold text-amber-300 mb-1">HeyGen API Key Not Configured</p>
        <p className="text-xs text-muted-foreground">Add HEYGEN_API_KEY to your environment secrets to enable AI video ads with real talking-head avatars.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="heygen-studio">
      <div className="rounded-xl bg-gradient-to-br from-violet-950/40 to-transparent border border-violet-500/20 p-5">
        <h3 className="text-base font-display font-bold text-foreground mb-4 flex items-center gap-2">
          <Wand2 size={16} className="text-violet-400" />
          HeyGen Video Ad Studio
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 font-normal">Primary</span>
        </h3>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">1. Select Avatar</label>
            {loadingAvatars ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
                <Loader2 size={16} className="animate-spin" /> Loading avatars…
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="avatar-grid">
                {avatars.map(a => (
                  <button
                    key={a.avatar_id}
                    data-testid={`avatar-${a.avatar_id}`}
                    onClick={() => setSelectedAvatarId(a.avatar_id)}
                    className={`relative rounded-xl overflow-hidden border-2 transition-all aspect-square ${selectedAvatarId === a.avatar_id ? "border-violet-500 ring-2 ring-violet-500/30" : "border-border hover:border-violet-500/40"}`}
                  >
                    {a.preview_image_url ? (
                      <img src={a.preview_image_url} alt={a.avatar_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted/50">
                        <User size={20} className="text-muted-foreground/50" />
                      </div>
                    )}
                    {selectedAvatarId === a.avatar_id && (
                      <div className="absolute inset-0 bg-violet-500/20 flex items-end justify-center pb-1">
                        <Check size={14} className="text-violet-300" />
                      </div>
                    )}
                  </button>
                ))}
                {avatars.length === 0 && (
                  <p className="col-span-full text-xs text-muted-foreground py-4">No avatars found. Check your HeyGen account.</p>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">2. Script</label>
            <div className="space-y-2">
              <div className="flex gap-2 items-start">
                <Textarea
                  data-testid="input-video-script"
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  placeholder="Type or generate your ad script. The avatar will speak this text."
                  className="min-h-[100px] bg-muted/30 border-border resize-none font-mono text-sm"
                />
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <Textarea
                  data-testid="input-video-brief"
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  placeholder="Brief for script generation (e.g. Facebook ad for a plumber targeting homeowners)…"
                  className="min-h-[50px] bg-muted/20 border-border resize-none text-xs flex-1"
                />
                <div className="flex gap-1 items-center">
                  <select
                    data-testid="select-video-duration"
                    value={duration}
                    onChange={e => setDuration(Number(e.target.value))}
                    className="bg-muted/50 border border-border rounded-lg px-2 py-1.5 text-xs text-foreground"
                  >
                    {DURATIONS.map(d => <option key={d} value={d}>{d}s</option>)}
                  </select>
                  <Button
                    data-testid="button-generate-script"
                    size="sm"
                    variant="outline"
                    disabled={generateScriptMutation.isPending || brief.length < 10}
                    onClick={() => generateScriptMutation.mutate()}
                    className="gap-1 text-xs whitespace-nowrap"
                  >
                    {generateScriptMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    Generate Script
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-4 items-end flex-wrap">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">Background Color</label>
              <input
                data-testid="input-bg-color"
                type="color"
                value={bgColor}
                onChange={e => setBgColor(e.target.value)}
                className="w-12 h-8 rounded cursor-pointer border border-border bg-transparent"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 block">Logo URL (optional)</label>
              <input
                data-testid="input-logo-url"
                type="url"
                value={logoUrl}
                onChange={e => setLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
                className="w-full bg-muted/30 border border-border rounded-lg px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50"
              />
            </div>
          </div>

          <Button
            data-testid="button-generate-heygen-video"
            disabled={generateVideoMutation.isPending || !selectedAvatarId || !script.trim() || videoStatus?.status === "rendering" || videoStatus?.status === "processing"}
            onClick={() => generateVideoMutation.mutate()}
            className="gap-2 bg-violet-600 hover:bg-violet-500 text-white w-full sm:w-auto"
          >
            {generateVideoMutation.isPending ? (
              <><Loader2 size={14} className="animate-spin" /> Starting generation…</>
            ) : (videoStatus?.status === "processing" || videoStatus?.status === "rendering") ? (
              <><Loader2 size={14} className="animate-spin" /> Rendering…</>
            ) : (
              <><Play size={14} /> Generate Video</>
            )}
          </Button>

          {videoStatus && videoStatus.status !== "completed" && videoStatus.status !== "failed" && (videoStatus.status === "rendering" || videoStatus.status === "processing" || videoStatus.status === "pending") && (
            <div className="rounded-xl bg-yellow-500/10 border border-yellow-500/20 p-4" data-testid="heygen-progress">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 size={14} className="animate-spin text-yellow-400" />
                <p className="text-sm font-medium text-yellow-300">Rendering your video…</p>
              </div>
              <div className="w-full bg-muted/40 rounded-full h-1.5">
                <div className="bg-yellow-400 h-1.5 rounded-full animate-pulse w-2/3" />
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">HeyGen typically takes 30–120 seconds. Checking every 10 seconds.</p>
            </div>
          )}

          {videoStatus?.status === "failed" && (
            <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-3" data-testid="heygen-error">
              <p className="text-sm text-red-400">{videoStatus.error || "Video generation failed."}</p>
            </div>
          )}

          {mp4Url && (
            <div className="rounded-xl bg-green-500/10 border border-green-500/20 p-4 space-y-3" data-testid="heygen-preview">
              <p className="text-sm font-semibold text-green-300 flex items-center gap-2"><Check size={14} /> Video Ready</p>
              <video
                data-testid="heygen-video-player"
                controls
                className="w-full max-w-sm rounded-lg mx-auto bg-black"
                src={mp4Url}
              />
              <div className="flex gap-2 flex-wrap">
                <Button
                  data-testid="button-save-to-library"
                  size="sm"
                  onClick={() => saveToLibrary.mutate()}
                  disabled={saveToLibrary.isPending}
                  className="gap-1.5 bg-green-600 hover:bg-green-500 text-white text-xs"
                >
                  {saveToLibrary.isPending ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  Save to Library
                </Button>
                <a
                  href={mp4Url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="link-heygen-download"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-muted/50 hover:bg-muted text-xs text-muted-foreground transition-all"
                >
                  <Copy size={12} /> Open in new tab
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VideoStudio() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const urlParamsInit = new URLSearchParams(window.location.search);
  const [mode, setMode] = useState<"heygen" | "did">(urlParamsInit.get("reelContentId") ? "did" : "heygen");
  const [platform, setPlatform] = useState("Instagram");
  const [editingConcept, setEditingConcept] = useState<VideoConcept | null>(null);
  const [editScript, setEditScript] = useState("");
  const [selectedVoice, setSelectedVoice] = useState("en-US-JennyNeural");
  const [expandedJob, setExpandedJob] = useState<number | null>(null);
  const reelContentId = urlParamsInit.get("reelContentId");
  const [autoReelLoaded, setAutoReelLoaded] = useState(false);

  const { data: videoJobs = [], isLoading: loadingJobs } = useQuery<VideoJob[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "videos"],
    queryFn: () => fetch(`/api/workspaces/${activeWorkspaceId}/videos`, { credentials: "include" }).then(r => r.json()),
    enabled: !!activeWorkspaceId,
    refetchInterval: 5000,
  });

  const generateConceptMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/videos/generate-concept`, {
        mode: "presenter",
        platform,
      });
      return res.json();
    },
    onSuccess: (data) => {
      setEditingConcept(data.concept);
      setEditScript(data.concept.script);
      toast({ title: "Video concept generated", description: `${data.concept.title} — ready for review` });
    },
    onError: (err: any) => {
      toast({ title: "Failed to generate concept", description: err.message, variant: "destructive" });
    },
  });

  const createVideoMutation = useMutation({
    mutationFn: async (concept: VideoConcept) => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/videos/create`, {
        concept: { ...concept, voiceId: selectedVoice },
        mode: "presenter",
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Video generation started", description: "This typically takes 1-3 minutes. The video will appear below when ready." });
      setEditingConcept(null);
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "videos"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed to start video generation", description: err.message, variant: "destructive" });
    },
  });

  const { data: creditsData } = useQuery({
    queryKey: ["/api/workspaces", activeWorkspaceId, "videos", "credits"],
    queryFn: () => fetch(`/api/workspaces/${activeWorkspaceId}/videos/credits/check`, { credentials: "include" }).then(r => r.json()),
    enabled: !!activeWorkspaceId,
  });

  const processingCount = videoJobs.filter(j => j.status === "PROCESSING").length;

  useEffect(() => {
    if (reelContentId && !autoReelLoaded && activeWorkspaceId && mode === "did") {
      setAutoReelLoaded(true);
      fetch(`/api/workspaces/${activeWorkspaceId}/content/${reelContentId}/reel-concept`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      }).then(r => r.json()).then(data => {
        if (data.concept) {
          setEditingConcept(data.concept);
          setEditScript(data.concept.script);
          toast({ title: 'Reel concept ready', description: `Adapted from your post — ${data.concept.title}` });
        }
      }).catch(() => {});
    }
  }, [reelContentId, autoReelLoaded, activeWorkspaceId, mode]);

  return (
    <div className="space-y-6" data-testid="video-studio">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card/50 border border-border rounded-xl p-4" data-testid="stat-total-videos">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
            <Video size={14} />
            Total Videos
          </div>
          <div className="text-2xl font-bold text-foreground">{videoJobs.length}</div>
        </div>
        <div className="bg-card/50 border border-border rounded-xl p-4" data-testid="stat-processing">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
            <Clock size={14} />
            Processing
          </div>
          <div className="text-2xl font-bold text-yellow-400">{processingCount}</div>
        </div>
        <div className="bg-card/50 border border-border rounded-xl p-4" data-testid="stat-credits">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
            <Sparkles size={14} />
            D-ID Credits
          </div>
          <div className="text-2xl font-bold text-green-400">{creditsData?.credits?.remaining ?? "—"}</div>
        </div>
      </div>

      <div className="flex gap-1 p-0.5 rounded-lg bg-muted/30 border border-border w-fit">
        <button
          data-testid="tab-heygen"
          onClick={() => setMode("heygen")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${mode === "heygen" ? "bg-violet-600/30 text-violet-300 border border-violet-500/30" : "text-muted-foreground hover:text-foreground"}`}
        >
          HeyGen Avatars
        </button>
        <button
          data-testid="tab-did"
          onClick={() => setMode("did")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${mode === "did" ? "bg-blue-600/30 text-blue-300 border border-blue-500/30" : "text-muted-foreground hover:text-foreground"}`}
        >
          D-ID / Veo
        </button>
      </div>

      {mode === "heygen" && <HeyGenStudio />}

      {mode === "did" && (
        <div className="bg-card/50 border border-border rounded-xl p-5" data-testid="did-video-creator">
          <h3 className="text-lg font-display font-bold text-foreground mb-4">Create Video with D-ID / Veo</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-muted-foreground text-xs mb-1.5">Platform</label>
              <div className="flex gap-1 flex-wrap" data-testid="platform-selector">
                {PLATFORMS.map(p => (
                  <button
                    key={p}
                    data-testid={`platform-${p.toLowerCase()}`}
                    onClick={() => setPlatform(p)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      platform === p
                        ? "bg-blue-600 text-white"
                        : "bg-muted/50 text-muted-foreground hover:text-muted-foreground"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-muted-foreground text-xs mb-1.5">Voice</label>
              <select
                data-testid="voice-selector"
                value={selectedVoice}
                onChange={e => setSelectedVoice(e.target.value)}
                className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground"
              >
                {VOICE_OPTIONS.map(v => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            data-testid="button-generate-concept"
            onClick={() => generateConceptMutation.mutate()}
            disabled={generateConceptMutation.isPending}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium text-sm hover:from-blue-500 hover:to-purple-500 transition-all disabled:opacity-50"
          >
            {generateConceptMutation.isPending ? (
              <><Loader2 size={16} className="animate-spin" /> Draper is creating your video concept...</>
            ) : (
              <><Sparkles size={16} /> Generate Video Concept with Draper</>
            )}
          </button>
          <p className="text-muted-foreground/70 text-[10px] mt-2 text-center">
            Uses D-ID • AI presenter speaks your script • {creditsData?.credits?.remaining ?? "?"} credits remaining
          </p>

          {editingConcept && (
            <div className="mt-4 bg-muted/20 border border-blue-500/30 rounded-xl p-5" data-testid="concept-editor">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-display font-bold text-foreground flex items-center gap-2">
                  <Edit3 size={16} className="text-blue-400" />
                  {editingConcept.title}
                  {editingConcept.aspectRatio === '9:16' && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300">9:16 Reel</span>}
                </h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-muted-foreground text-xs mb-1.5">Script</label>
                  <textarea
                    data-testid="input-script"
                    value={editScript}
                    onChange={e => setEditScript(e.target.value)}
                    rows={5}
                    className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground resize-none font-mono"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="button-generate-video"
                    onClick={() => {
                      const updated = { ...editingConcept, script: editScript };
                      createVideoMutation.mutate(updated);
                    }}
                    disabled={createVideoMutation.isPending}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg font-medium text-sm hover:bg-green-500 transition-all disabled:opacity-50"
                  >
                    {createVideoMutation.isPending ? (
                      <><Loader2 size={16} className="animate-spin" /> Starting...</>
                    ) : (
                      <><Play size={16} /> Generate Video</>
                    )}
                  </button>
                  <button
                    data-testid="button-regenerate-concept"
                    onClick={() => generateConceptMutation.mutate()}
                    disabled={generateConceptMutation.isPending}
                    className="flex items-center gap-2 px-4 py-3 bg-muted text-muted-foreground rounded-lg text-sm"
                  >
                    <RefreshCw size={14} />
                    New Concept
                  </button>
                  <button
                    data-testid="button-discard-concept"
                    onClick={() => setEditingConcept(null)}
                    className="flex items-center gap-2 px-4 py-3 bg-muted/50 text-muted-foreground rounded-lg text-sm"
                  >
                    Discard
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {videoJobs.length > 0 && (
        <div>
          <h3 className="text-lg font-display font-bold text-foreground mb-3">Generated Videos (D-ID)</h3>
          <div className="space-y-3">
            {videoJobs.map((job) => (
              <div
                key={job.id}
                data-testid={`video-job-${job.id}`}
                className="bg-card/50 border border-border rounded-xl overflow-hidden"
              >
                <div
                  className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/30 transition-all"
                  onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    job.status === "COMPLETED" ? "bg-green-500/20" :
                    job.status === "PROCESSING" ? "bg-yellow-500/20" :
                    job.status === "FAILED" ? "bg-red-500/20" : "bg-muted"
                  }`}>
                    {job.status === "COMPLETED" && <Check size={18} className="text-green-400" />}
                    {job.status === "PROCESSING" && <Loader2 size={18} className="text-yellow-400 animate-spin" />}
                    {job.status === "FAILED" && <AlertCircle size={18} className="text-red-400" />}
                    {job.status === "PENDING" && <Clock size={18} className="text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{job.concept?.title || `Video #${job.id}`}</div>
                    <div className="text-[10px] text-muted-foreground/70">
                      {job.concept?.platform} • {new Date(job.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className={`text-xs font-medium px-2 py-1 rounded-full ${
                    job.status === "COMPLETED" ? "bg-green-500/20 text-green-400" :
                    job.status === "PROCESSING" ? "bg-yellow-500/20 text-yellow-400" :
                    job.status === "FAILED" ? "bg-red-500/20 text-red-400" : "bg-muted text-muted-foreground"
                  }`} data-testid={`video-status-${job.id}`}>
                    {job.status}
                  </div>
                  {expandedJob === job.id ? <ChevronUp size={16} className="text-muted-foreground/70" /> : <ChevronDown size={16} className="text-muted-foreground/70" />}
                </div>
                {expandedJob === job.id && (
                  <div className="border-t border-border p-4 space-y-3">
                    {job.status === "COMPLETED" && job.videoUrl && (
                      <div data-testid={`video-player-${job.id}`}>
                        <video controls className="w-full max-w-xl rounded-lg bg-background mx-auto" src={job.videoUrl} />
                      </div>
                    )}
                    {job.status === "FAILED" && job.error && (
                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                        <p className="text-xs text-red-400">{job.error}</p>
                      </div>
                    )}
                    {job.status === "PROCESSING" && (
                      <div className="flex items-center gap-2 text-yellow-400 text-sm">
                        <Loader2 size={14} className="animate-spin" />
                        Video is being generated...
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
